"use client";

import type { CSSProperties, JSX, KeyboardEvent, PointerEvent } from "react";
import { useCallback, useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { useTranslations } from "next-intl";
import posthog from "posthog-js";
import { IconButton } from "@repo/ui/icon-button";
import { LinkButton } from "@repo/ui/link-button";
import { buildLinkOverlay, buildTextLayer, createPdfLoadingTask, getPageAspectRatio, getPageBaseWidth, isRenderCancellation, startPageRender, type PDFDocumentLoadingTask, type PDFDocumentProxy, type RenderTask, type TextLayerHandle } from "@/lib/pdf-viewer";
type ViewerStatus = "loading" | "ready" | "error";
const ZOOM_MIN = 1;
const ZOOM_MAX = 3;
const ZOOM_STEP = 0.5;
const clampZoom = (value: number): number => Math.min(ZOOM_MAX, Math.max(ZOOM_MIN, value));
export interface InteractivePdfViewerProps {
  title: string;
  coverHeadline?: string;
  coverDescription?: string;
  pdfUrl: string;
  allowDownload: boolean;
}
export function InteractivePdfViewer({
  title,
  coverHeadline,
  coverDescription,
  pdfUrl,
  allowDownload
}: InteractivePdfViewerProps): JSX.Element {
  const t = useTranslations("interactivePdf");
  // "loading" is the initial state (no idle phase) — the gate scrim covers
  // the frame until "Start reading", and loading kicks off as soon as the
  // block nears the viewport.
  const [status, setStatus] = useState<ViewerStatus>("loading");
  const [numPages, setNumPages] = useState(0);
  const [currentPage, setCurrentPage] = useState(1);
  const [expanded, setExpanded] = useState(false);
  // The PDF loads and renders behind the scrim; "Start reading" only lifts
  // the scrim and unlocks scrolling.
  const [started, setStarted] = useState(false);
  const [loadProgress, setLoadProgress] = useState<number | null>(null);
  // Committed zoom. Pinch previews via the --pdf-zoom var (layout-only) and
  // commits here on gesture end, which re-renders canvases crisp at scale.
  const [zoom, setZoom] = useState(1);
  const zoomRef = useRef(1);
  const docRef = useRef<PDFDocumentProxy | null>(null);
  const loadingTaskRef = useRef<PDFDocumentLoadingTask | null>(null);
  const scrollRef = useRef<HTMLDivElement | null>(null);
  const overlayRef = useRef<HTMLDivElement | null>(null);
  const holdersRef = useRef<(HTMLDivElement | null)[]>([]);
  const textLayerElsRef = useRef<(HTMLDivElement | null)[]>([]);
  const linkLayerElsRef = useRef<(HTMLDivElement | null)[]>([]);
  const textLayersRef = useRef<Map<number, TextLayerHandle>>(new Map());
  const pageBaseWidthsRef = useRef<Map<number, number>>(new Map());
  const renderTasksRef = useRef<Map<number, RenderTask>>(new Map());
  const renderedRef = useRef<Set<number>>(new Set());
  // Latest renderPage, so the deferred retry can call it without renderPage
  // having to depend on itself.
  const renderPageRef = useRef<((pageNumber: number) => Promise<void>) | null>(null);
  const currentPageRef = useRef(1);
  const mountedRef = useRef(true);
  // Callback ref that always points to the live Expand/Close button (it
  // remounts across the portal), so focus can return to it on close.
  const expandButtonRef = useRef<HTMLButtonElement | null>(null);
  // Doc-level ratio (page 1) used for skeleton placeholders before each page
  // renders at its own ratio. In a ref so imperative eviction reads it fresh.
  const skeletonRatioRef = useRef("0.707");
  const start = useCallback(async () => {
    try {
      // Expose the loading task before awaiting the document so unmount can
      // cancel an in-flight load. If we unmounted during the lazy import
      // (before the ref could be set), destroy the task here.
      const loadingTask = await createPdfLoadingTask(pdfUrl);
      if (!mountedRef.current) {
        void loadingTask.destroy();
        return;
      }
      loadingTaskRef.current = loadingTask;
      loadingTask.onProgress = ({
        loaded,
        total
      }: {
        loaded: number;
        total: number;
      }) => {
        if (mountedRef.current && total > 0) {
          setLoadProgress(Math.min(99, Math.round(loaded / total * 100)));
        }
      };
      const doc = await loadingTask.promise;
      if (!mountedRef.current) return;
      docRef.current = doc;
      skeletonRatioRef.current = String(await getPageAspectRatio(doc));
      if (!mountedRef.current) return;
      setNumPages(doc.numPages);
      setStatus("ready");
    } catch (error) {
      if (!mountedRef.current) return;
      console.error("[interactive-pdf] failed to load document", error);
      setStatus("error");
    }
  }, [pdfUrl]);

  // Page is no longer wanted if it was evicted mid-render or the holder was
  // replaced by a remount (expand/collapse) while awaiting pdfjs.
  const isRenderStillWanted = useCallback((pageNumber: number, holder: HTMLDivElement) => renderedRef.current.has(pageNumber) && holdersRef.current[pageNumber - 1] === holder, []);
  const renderPage = useCallback(async (pageNumber_0: number) => {
    const doc_0 = docRef.current;
    const holder_0 = holdersRef.current[pageNumber_0 - 1];
    if (!doc_0 || !holder_0 || renderedRef.current.has(pageNumber_0)) return;
    // Layout isn't always settled when the observer fires (portalled
    // fullscreen frame, or a document the browser hasn't laid out because
    // it's hidden). Rendering at ~0 width yields a 1x1 canvas and a bogus
    // aspect ratio that sticks. Don't reserve the page — retry next frame,
    // which is also when a hidden document resumes.
    const width = holder_0.clientWidth;
    if (!(width > 1)) {
      requestAnimationFrame(() => void renderPageRef.current?.(pageNumber_0));
      return;
    }
    renderedRef.current.add(pageNumber_0);
    try {
      const render = await startPageRender(doc_0, pageNumber_0, width);
      // A null render (no 2d context) leaves nothing scheduled — drop the
      // reservation so the page can retry later.
      if (!render) {
        renderedRef.current.delete(pageNumber_0);
        return;
      }
      // Evicted or remounted while awaiting getPage — discard the canvas.
      // Consume the rejection: nothing awaits this task's promise, so the
      // cancellation would otherwise surface as an unhandled rejection.
      if (!isRenderStillWanted(pageNumber_0, holder_0)) {
        render.task.promise.catch(() => undefined);
        render.task.cancel();
        return;
      }
      renderTasksRef.current.set(pageNumber_0, render.task);
      await render.task.promise;
      renderTasksRef.current.delete(pageNumber_0);
      if (!isRenderStillWanted(pageNumber_0, holder_0)) return;
      // Match the holder box to this page's own ratio so mixed
      // portrait/landscape pages are not stretched. Guarded: a degenerate
      // canvas must never overwrite a good ratio (it would persist, since
      // eviction deliberately keeps the page's known ratio).
      if (render.canvas.width > 1 && render.canvas.height > 1) {
        holder_0.style.aspectRatio = `${render.canvas.width} / ${render.canvas.height}`;
      }
      // Canvas only — the text/link layers are React-owned children of the
      // holder and must survive canvas swaps and eviction.
      holder_0.querySelector("canvas")?.remove();
      holder_0.prepend(render.canvas);
      holder_0.dataset.state = "rendered";
    } catch (error_0) {
      renderedRef.current.delete(pageNumber_0);
      if (!isRenderCancellation(error_0)) {
        console.error(`[interactive-pdf] render failed for page ${pageNumber_0}`, error_0);
      }
    }
  }, [isRenderStillWanted]);
  useEffect(() => {
    renderPageRef.current = renderPage;
  }, [renderPage]);
  const evictPage = useCallback((pageNumber_1: number) => {
    const holder_1 = holdersRef.current[pageNumber_1 - 1];
    renderTasksRef.current.get(pageNumber_1)?.cancel();
    renderTasksRef.current.delete(pageNumber_1);
    renderedRef.current.delete(pageNumber_1);
    if (holder_1) {
      // Reclaim only the canvas; keep the holder's page ratio (it is exact
      // once known) and the text/link layers, so find/select and TOC links
      // keep working across the whole document.
      holder_1.querySelector("canvas")?.remove();
      holder_1.dataset.state = "pending";
    }
  }, []);
  useEffect(() => {
    if (status !== "ready") return;
    const root = scrollRef.current;
    const holders = holdersRef.current.slice(0, numPages).filter(Boolean) as HTMLDivElement[];
    if (!root || holders.length === 0) return;
    const renderTasks = renderTasksRef.current;
    const rendered = renderedRef.current;
    const renderObserver = new IntersectionObserver(entries => {
      for (const entry of entries) {
        const pageNumber_2 = Number((entry.target as HTMLElement).dataset.page);
        if (entry.isIntersecting) {
          void renderPage(pageNumber_2);
        } else if (renderedRef.current.has(pageNumber_2)) {
          evictPage(pageNumber_2);
        }
      }
    }, {
      root,
      rootMargin: "200% 0px"
    });

    // "Current page" = the one crossing the frame's vertical midpoint. A
    // threshold-based observer can't work here: a portrait page taller than
    // the frame never reaches 50% visibility, so the counter would stick.
    const counterObserver = new IntersectionObserver(entries_0 => {
      for (const entry_0 of entries_0) {
        if (entry_0.isIntersecting) {
          const pageNumber_3 = Number((entry_0.target as HTMLElement).dataset.page);
          currentPageRef.current = pageNumber_3;
          setCurrentPage(pageNumber_3);
        }
      }
    }, {
      root,
      rootMargin: "-50% 0px -50% 0px",
      threshold: 0
    });

    // The reader is portalled on expand/collapse, so the frame remounts.
    // Restore the reader to the page it was on before observing. The frame is
    // the holders' offsetParent (position: relative), so holder.offsetTop is
    // already frame-relative — no further offset subtraction.
    const restorePage = currentPageRef.current;
    if (restorePage > 1) {
      const holder_2 = holdersRef.current[restorePage - 1];
      if (holder_2) root.scrollTop = holder_2.offsetTop;
    }
    for (const holder_3 of holders) {
      renderObserver.observe(holder_3);
      counterObserver.observe(holder_3);
    }
    void renderPage(restorePage);
    return () => {
      renderObserver.disconnect();
      counterObserver.disconnect();
      for (const task of renderTasks.values()) task.cancel();
      renderTasks.clear();
      rendered.clear();
      for (const holder_4 of holders) holder_4.dataset.state = "pending";
    };
    // `expanded` and `zoom` rebuild the observers so re-`observe()` re-fires
    // for the visible pages and re-renders them at the new display width.
    // (On zoom the old canvas stays up until its crisp replacement lands.)
  }, [status, numPages, expanded, zoom, renderPage, evictPage]);
  const commitZoom = useCallback((value: number) => {
    const next = clampZoom(value);
    if (next === zoomRef.current) return;
    zoomRef.current = next;
    setZoom(next);
    // Keep the reader anchored on the page being viewed — zooming reflows
    // every holder above it.
    const root_0 = scrollRef.current;
    const holder_5 = holdersRef.current[currentPageRef.current - 1];
    requestAnimationFrame(() => {
      if (root_0 && holder_5) root_0.scrollTop = holder_5.offsetTop;
    });
  }, []);

  // Rescale existing text layers to the new display width (canvases re-render
  // via the observer effect; link overlays are percent-positioned and free).
  useEffect(() => {
    if (status !== "ready") return;
    for (const [pageNumber_4, handle] of textLayersRef.current) {
      const holder_6 = holdersRef.current[pageNumber_4 - 1];
      const baseWidth = pageBaseWidthsRef.current.get(pageNumber_4);
      if (!holder_6 || !baseWidth) continue;
      handle.setScale(holder_6.clientWidth / baseWidth || 1);
    }
  }, [status, zoom]);

  // Two-pointer pinch: previews by writing the --pdf-zoom var (pure layout,
  // no React re-render per move), commits on gesture end. touch-action on the
  // frame suppresses the browser's own pinch-zoom so the events reach us.
  const pointersRef = useRef<Map<number, {
    x: number;
    y: number;
  }>>(new Map());
  const pinchRef = useRef<{
    startDist: number;
    startZoom: number;
    pending: number;
  } | null>(null);
  const pointerDistance = () => {
    const [a, b] = Array.from(pointersRef.current.values());
    if (!a || !b) return 0;
    return Math.hypot(a.x - b.x, a.y - b.y);
  };
  const handlePointerDown = useCallback((event: PointerEvent<HTMLDivElement>) => {
    if (!started || event.pointerType !== "touch") return;
    pointersRef.current.set(event.pointerId, {
      x: event.clientX,
      y: event.clientY
    });
    if (pointersRef.current.size === 2) {
      pinchRef.current = {
        startDist: pointerDistance(),
        startZoom: zoomRef.current,
        pending: zoomRef.current
      };
    }
  }, [started]);
  const handlePointerMove = useCallback((event_0: PointerEvent<HTMLDivElement>) => {
    const pinch = pinchRef.current;
    if (!pointersRef.current.has(event_0.pointerId)) return;
    pointersRef.current.set(event_0.pointerId, {
      x: event_0.clientX,
      y: event_0.clientY
    });
    if (!pinch || pointersRef.current.size !== 2 || pinch.startDist === 0) return;
    pinch.pending = clampZoom(pinch.startZoom * (pointerDistance() / pinch.startDist));
    scrollRef.current?.style.setProperty("--pdf-zoom", String(pinch.pending));
  }, []);
  const handlePointerEnd = useCallback((event_1: PointerEvent<HTMLDivElement>) => {
    pointersRef.current.delete(event_1.pointerId);
    const pinch_0 = pinchRef.current;
    if (pinch_0 && pointersRef.current.size < 2) {
      pinchRef.current = null;
      commitZoom(pinch_0.pending);
      // Sync the var with committed state (commitZoom no-ops on equal).
      scrollRef.current?.style.setProperty("--pdf-zoom", String(clampZoom(pinch_0.pending)));
    }
  }, [commitZoom]);
  useEffect(() => {
    // Reset on (re)mount — StrictMode's mount→cleanup→mount cycle would
    // otherwise leave mountedRef false and wedge start() on "loading".
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
      void loadingTaskRef.current?.destroy();
    };
  }, []);

  // Defer the document (and the pdfjs chunk) until the block nears the
  // viewport, mirroring MuxVideoPlayer — a multi-MB brochure shouldn't cost
  // visitors who never scroll to it. Once near, loading starts immediately so
  // the gate's first-page cover is usually ready by the time it's on screen.
  // The ref guards StrictMode's double-run.
  const loadRequestedRef = useRef(false);
  const ensureLoad = useCallback(() => {
    if (loadRequestedRef.current) return;
    loadRequestedRef.current = true;
    void start();
  }, [start]);
  useEffect(() => {
    if (loadRequestedRef.current) return;
    const el = overlayRef.current;
    if (typeof IntersectionObserver === "undefined" || !el) {
      queueMicrotask(ensureLoad);
      return;
    }
    const observer = new IntersectionObserver(entries_1 => {
      if (entries_1.some(entry_1 => entry_1.isIntersecting)) {
        ensureLoad();
        observer.disconnect();
      }
    }, {
      rootMargin: "600px 0px"
    });
    observer.observe(el);
    return () => observer.disconnect();
  }, [ensureLoad]);
  const handleStartReading = useCallback(() => {
    // Belt-and-braces: IntersectionObserver is suspended in hidden/background
    // documents, so an interaction must also be able to request the load.
    ensureLoad();
    setStarted(true);
    requestAnimationFrame(() => scrollRef.current?.focus());
  }, [ensureLoad]);

  // Fullscreen portal is a modal: lock body scroll, make the rest of the page
  // inert (so background content is unreachable by tab/AT), trap Tab within
  // the overlay, close on Escape, and restore focus to the trigger on close.
  useEffect(() => {
    if (!expanded) return;
    const overlay = overlayRef.current;
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    const inerted: Element[] = [];
    if (overlay) {
      for (const child of Array.from(document.body.children)) {
        if (child !== overlay && !child.hasAttribute("inert")) {
          child.setAttribute("inert", "");
          inerted.push(child);
        }
      }
    }
    const getFocusable = () => overlay ? Array.from(overlay.querySelectorAll<HTMLElement>('a[href], button:not([disabled]), [tabindex]:not([tabindex="-1"])')).filter(el_0 => el_0.offsetParent !== null) : [];
    const onKeyDown = (event_2: globalThis.KeyboardEvent) => {
      if (event_2.key === "Escape") {
        setExpanded(false);
        return;
      }
      if (event_2.key !== "Tab" || !overlay) return;
      const focusable = getFocusable();
      if (focusable.length === 0) return;
      const first = focusable[0]!;
      const last = focusable[focusable.length - 1]!;
      const active = document.activeElement;
      if (event_2.shiftKey && (active === first || !overlay.contains(active))) {
        event_2.preventDefault();
        last.focus();
      } else if (!event_2.shiftKey && active === last) {
        event_2.preventDefault();
        first.focus();
      }
    };
    document.addEventListener("keydown", onKeyDown);
    (getFocusable()[0] ?? scrollRef.current)?.focus();
    return () => {
      document.body.style.overflow = previousOverflow;
      document.removeEventListener("keydown", onKeyDown);
      for (const el_1 of inerted) el_1.removeAttribute("inert");
    };
  }, [expanded]);

  // Restore focus to the Expand button after collapsing. Runs after the
  // collapsed reader has remounted its button (the portal detached the old
  // one), reading the ref lazily inside rAF to get the fresh node.
  const wasExpandedRef = useRef(false);
  useEffect(() => {
    if (!expanded && wasExpandedRef.current) {
      requestAnimationFrame(() => expandButtonRef.current?.focus());
    }
    wasExpandedRef.current = expanded;
  }, [expanded]);
  const scrollToPage = useCallback((pageNumber_5: number) => {
    const root_1 = scrollRef.current;
    const holder_7 = holdersRef.current[pageNumber_5 - 1];
    if (!root_1 || !holder_7) return;
    const reducedMotion = window.matchMedia("(prefers-reduced-motion: reduce)").matches;
    root_1.scrollTo({
      // Frame is the holder's offsetParent, so offsetTop is already
      // frame-relative.
      top: holder_7.offsetTop,
      behavior: reducedMotion ? "auto" : "smooth"
    });
  }, []);

  // Text + link layers are built eagerly for every page (unlike canvases):
  // they're cheap DOM, and doc-wide find-in-page, selection and screen-reader
  // access only work if the text exists for pages that never rendered.
  // Rebuilt when the holders remount (expand/collapse portals the frame).
  useEffect(() => {
    if (status !== "ready") return;
    const doc_1 = docRef.current;
    if (!doc_1) return;
    let cancelled = false;
    const handles = textLayersRef.current;
    const textEls = textLayerElsRef.current;
    const linkEls = linkLayerElsRef.current;
    const build = async () => {
      for (let pageNumber_6 = 1; pageNumber_6 <= numPages; pageNumber_6++) {
        if (cancelled) return;
        const textEl = textEls[pageNumber_6 - 1];
        const linkEl = linkEls[pageNumber_6 - 1];
        const holder_8 = holdersRef.current[pageNumber_6 - 1];
        if (!textEl || !linkEl || !holder_8) continue;
        try {
          let baseWidth_0 = pageBaseWidthsRef.current.get(pageNumber_6);
          if (baseWidth_0 === undefined) {
            baseWidth_0 = await getPageBaseWidth(doc_1, pageNumber_6);
            pageBaseWidthsRef.current.set(pageNumber_6, baseWidth_0);
          }
          if (cancelled) return;
          const scale = holder_8.clientWidth / baseWidth_0 || 1;
          const handle_0 = await buildTextLayer(doc_1, pageNumber_6, textEl, scale);
          if (cancelled) {
            handle_0.cancel();
            return;
          }
          handles.set(pageNumber_6, handle_0);
          await buildLinkOverlay(doc_1, pageNumber_6, linkEl, {
            onInternalLink: scrollToPage,
            internalLabel: targetPage => t("linkToPage", {
              page: targetPage
            })
          });
        } catch (error_1) {
          console.error(`[interactive-pdf] layer build failed for page ${pageNumber_6}`, error_1);
        }
      }
    };
    void build();
    return () => {
      cancelled = true;
      for (const handle_1 of handles.values()) handle_1.cancel();
      handles.clear();
      for (const el_2 of textEls) el_2?.replaceChildren();
      for (const el_3 of linkEls) el_3?.replaceChildren();
    };
  }, [status, numPages, expanded, scrollToPage, t]);
  const handleFrameKeyDown = useCallback((event_3: KeyboardEvent<HTMLDivElement>) => {
    if (event_3.key === "+" || event_3.key === "=") {
      event_3.preventDefault();
      commitZoom(zoomRef.current + ZOOM_STEP);
      return;
    }
    if (event_3.key === "-" || event_3.key === "_") {
      event_3.preventDefault();
      commitZoom(zoomRef.current - ZOOM_STEP);
      return;
    }
    const pageJumps: Record<string, number> = {
      PageDown: Math.min(currentPageRef.current + 1, numPages),
      PageUp: Math.max(currentPageRef.current - 1, 1),
      Home: 1,
      End: numPages
    };
    const target = pageJumps[event_3.key];
    if (target === undefined) return;
    event_3.preventDefault();
    scrollToPage(target);
  }, [numPages, scrollToPage, commitZoom]);
  const handleDownload = useCallback(() => {
    posthog.capture("brochure_downloaded", {
      brochure_title: title
    });
  }, [title]);

  // The `download` attribute is ignored cross-origin, so force a download via
  // Sanity's `?dl=` param instead of just opening the PDF.
  const downloadUrl = `${pdfUrl}${pdfUrl.includes("?") ? "&" : "?"}dl=`;
  const header = <div className="flex flex-wrap items-center justify-between gap-4 border-b border-border-default bg-surface-base-primary px-4 py-3">
      <div>
        {title ? <h2 className="typography-desktop-heading-subtitle-1 text-text-primary">
            {title}
          </h2> : null}
        <p className="typography-component-xxs-medium text-text-secondary uppercase tracking-wider">
          {numPages > 0 ? t("pdfPages", {
          count: numPages
        }) : t("pdf")}
        </p>
      </div>
      <div className="flex items-center gap-3">
        {status === "ready" ? <div className="flex items-center gap-1">
            <IconButton size="sm" icon="minus" aria-label={t("zoomOut")} disabled={zoom <= ZOOM_MIN} onClick={() => commitZoom(zoomRef.current - ZOOM_STEP)} />
            <button type="button" aria-label={t("zoomReset")} disabled={zoom === 1} onClick={() => commitZoom(1)} className="typography-component-xxs-medium min-w-[4ch] text-center text-text-secondary tabular-nums">
              {Math.round(zoom * 100)}%
            </button>
            <IconButton size="sm" icon="plus" aria-label={t("zoomIn")} disabled={zoom >= ZOOM_MAX} onClick={() => commitZoom(zoomRef.current + ZOOM_STEP)} />
          </div> : null}
        {status === "ready" ? <span aria-live="polite" aria-label={t("pageOf", {
        current: currentPage,
        total: numPages
      })} className="typography-component-xxs-medium text-text-secondary tabular-nums">
            {currentPage} / {numPages}
          </span> : null}
        {status === "ready" ? <LinkButton asChild size="sm" iconLeft={expanded ? "close" : "expand-all"}>
            <button ref={expandButtonRef} type="button" onClick={() => {
          // Expanding while gated implies intent to read — lift the
          // scrim.
          if (!started) handleStartReading();
          setExpanded(!expanded);
        }} aria-label={expanded ? t("exitFullScreen") : t("readFullScreen")}>
              {expanded ? t("close") : t("expand")}
            </button>
          </LinkButton> : null}
        {allowDownload ? <LinkButton href={downloadUrl} rel="noopener noreferrer" size="sm" iconLeft="arrow-down" onClick={handleDownload}>
            {t("download")}
          </LinkButton> : null}
      </div>
    </div>;

  // Gated: the frame is visible (page 1 renders beneath the scrim) but
  // scroll-locked, out of the tab order and non-interactive until "Start
  // reading" lifts it. The frame is also `isolate`d so the page layers'
  // stacking can never paint over — or steal clicks from — the scrim.
  const gated = !started;
  const frame = <div ref={scrollRef} tabIndex={gated ? -1 : 0} role="region" aria-label={title ? t("regionPages", {
    title
  }) : t("brochurePages")} onKeyDown={handleFrameKeyDown} onPointerDown={handlePointerDown} onPointerMove={handlePointerMove} onPointerUp={handlePointerEnd} onPointerCancel={handlePointerEnd} style={{
    "--pdf-zoom": zoom,
    // pan-x/pan-y keep native scrolling but suppress the browser's own
    // pinch-zoom inside the frame so the pinch reaches our handlers.
    touchAction: gated ? undefined : "pan-x pan-y"
  } as CSSProperties} className={`relative isolate flex h-full flex-col gap-3 overscroll-contain bg-surface-base-secondary p-3 focus-visible:outline-none tablet:gap-4 tablet:p-4 ${gated ? "overflow-hidden pointer-events-none" : "overflow-x-auto overflow-y-auto"}`}>
      {numPages === 0 ? <div className="h-full w-full shrink-0 animate-pulse bg-surface-base-primary" /> : null}
      {Array.from({
      length: numPages
    }, (_, index) => <div key={index} ref={el_4 => {
      holdersRef.current[index] = el_4;
      // Seed the skeleton ratio on mount; the rendered guard keeps
      // per-page ratios from being clobbered on re-render.
      if (el_4 && el_4.dataset.state !== "rendered") {
        el_4.style.aspectRatio = skeletonRatioRef.current;
      }
    }} data-page={index + 1} data-state="pending"
    // Width tracks --pdf-zoom so pinch previews are pure layout;
    // committed zoom re-renders canvases crisp at the new width.
    style={{
      width: "calc(var(--pdf-zoom, 1) * 100%)"
    }} className="relative shrink-0 animate-pulse bg-surface-base-primary shadow-sm data-[state=rendered]:animate-none">
          {/* Persistent layers — the canvas is prepended/removed around them
              imperatively. textLayer = selectable/AT text; pdfLinkLayer =
              the PDF's own link annotations (percent-positioned). */}
          <div ref={el_5 => {
        textLayerElsRef.current[index] = el_5;
      }} className="textLayer" />
          <div ref={el_6 => {
        linkLayerElsRef.current[index] = el_6;
      }} className="pdfLinkLayer" />
        </div>)}
    </div>;

  // The first page shows through the translucent blur, matching the demo's
  // cover treatment (no separate poster image).
  const scrim = <div className="absolute inset-0 z-10 flex flex-col items-center justify-center gap-4 bg-surface-base-secondary/55 p-6 text-center backdrop-blur-[7px]">
      {status === "error" ? <p className="typography-body-md-regular rounded-sm bg-surface-base-primary px-4 py-2 text-text-primary">
          {t("errorBody")}
          {allowDownload ? <>
              {" "}
              <a href={downloadUrl} className="underline">
                {t("downloadInstead")}
              </a>
            </> : null}
        </p> : <>
          {coverHeadline || title ? <p className="typography-mobile-editorial-h4 max-w-xl text-text-primary">
              {coverHeadline || title}
            </p> : null}
          <p className="typography-component-xxs-medium text-text-secondary uppercase tracking-wider">
            {numPages > 0 ? `${t("pages", {
          count: numPages
        })} · ` : ""}
            {coverDescription || t("coverDefaultDescription")}
          </p>
          <LinkButton asChild size="md" underline>
            <button type="button" onClick={handleStartReading}>
              {t("startReading")}
            </button>
          </LinkButton>
          {status === "loading" ? <p aria-live="polite" className="typography-component-xxs-medium text-text-secondary uppercase tracking-wider tabular-nums">
              {t("loadingBrochure")}
              {loadProgress !== null ? ` · ${loadProgress}%` : "…"}
            </p> : null}
        </>}
    </div>;
  const reader = <div ref={overlayRef} {...expanded ? {
    role: "dialog",
    "aria-modal": true,
    "aria-label": title || t("brochureReader")
  } : {}} className={expanded ? "fixed inset-0 z-[100] flex flex-col bg-surface-base-secondary" : "flex flex-col border border-border-default"}>
      {header}
      <div className={`relative ${expanded ? "min-h-0 flex-1" : "h-[37.5rem] max-h-[80vh]"}`}>
        {frame}
        {gated || status === "error" ? scrim : null}
      </div>
    </div>;

  // Portal to body on expand so the fullscreen overlay escapes any ancestor
  // stacking context (the site nav sits at z-50 in its own context).
  if (expanded) {
    return <>
        <div aria-hidden className="h-[37.5rem] max-h-[80vh] border border-border-default bg-surface-base-secondary" />
        {createPortal(reader, document.body)}
      </>;
  }
  return reader;
}